<template>
  <section class="section is-medium pricing-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.pricing-title') }}
        </h2>
        <p class="subtitle is-5 animate fade-in-up animate--delay-1">
          Choose the perfect plan for your music career
        </p>
      </div>

      <!-- Pricing Highlight Banner -->
      <div class="pricing-highlight-banner animate fade-in-up animate--delay-1">
        <div class="banner-content">
          <div class="banner-icon">
            <i class="fas fa-star" />
          </div>
          <div class="banner-text">
            <h3 class="title is-4">
              Best Value in Music Distribution
            </h3>
            <p>Start free, scale as you grow. No hidden fees, no surprises.</p>
          </div>
        </div>
      </div>

      <div class="columns is-multiline is-centered">
        <!-- Free Tier -->
        <div class="column is-4">
          <div class="pricing-card animate fade-in-up">
            <div class="pricing-header">
              <h3 class="pricing-tier">
                Free
              </h3>
              <div class="pricing-price">
                <span class="price">$0</span>
                <span class="billing">/{{ $t('general.year') }}</span>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.free') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> 85% {{ $t('royalties.royalty-split') }}</li>
                <li><i class="fas fa-check" /> 1 {{ $t('navigation.artists') }}</li>
                <li><i class="fas fa-check" /> 1 {{ $t('releases.release') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> {{ $t('royalties.min-payment') }}: $100</li>
                <li class="disabled">
                  <i class="fas fa-times" /> {{ $t('settings.custom-label') }}
                </li>
                <li class="disabled">
                  <i class="fas fa-times" /> {{ $t('settings.social-verification') }}
                </li>
              </ul>
            </div>
            <div class="pricing-cta">
              <router-link
                to="/upgrade?from=v3_landing"
                class="button is-primary is-fullwidth"
              >
                {{ $t('landing.cta-primary') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- PRO Tier -->
        <div class="column is-4">
          <div class="pricing-card is-featured animate fade-in-up animate--delay-1">
            <div class="popular-badge">
              {{ $t('general.popular') }}
            </div>
            <div class="pricing-header">
              <h3 class="pricing-tier">
                PRO
              </h3>
              <div class="pricing-price">
                <span class="price">$49</span>
                <span class="billing">/{{ $t('general.year') }}</span>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.pro') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> 100% {{ $t('royalties.royalty-split') }}*</li>
                <li><i class="fas fa-check" /> 5 {{ $t('navigation.artists') }}</li>
                <li><i class="fas fa-check" /> 25 {{ $t('navigation.releases') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.custom-label') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.social-verification') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.premium-stores') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.content-id') }}</li>
                <li><i class="fas fa-check" /> {{ $t('royalties.min-payment') }}: $50</li>
                <template v-if="isProFeaturesExpanded">
                  <li><i class="fas fa-check" /> {{ $t('settings.ai-tools') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.social-platforms') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.faster-payments') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.faster-releases') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.dedicated-support') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.all-features') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.presave') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.playlist-submission') }}</li>
                </template>
                <li class="expand-features">
                  <button
                    class="expand-button"
                    @click="isProFeaturesExpanded = !isProFeaturesExpanded"
                  >
                    <span>{{ isProFeaturesExpanded ? $t('general.show-less') : $t('general.show-more') }}</span>
                    <i
                      class="fas"
                      :class="isProFeaturesExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
                    />
                  </button>
                </li>
              </ul>
            </div>
            <div class="pricing-cta">
              <router-link
                to="/upgrade?from=v3_landing"
                class="button is-primary is-fullwidth"
              >
                {{ $t('settings.change-plan') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- Advanced Tier -->
        <div class="column is-4">
          <div class="pricing-card animate fade-in-up animate--delay-2">
            <div class="pricing-header">
              <h3 class="pricing-tier">
                Advanced
              </h3>
              <div class="pricing-price">
                <span class="price">$99</span>
                <span class="billing">/{{ $t('general.year') }}</span>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.advanced') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> {{ $t('settings.pro-features') }}</li>
                <li><i class="fas fa-check" /> 100 {{ $t('navigation.releases') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> 50 {{ $t('navigation.artists') }}</li>
              </ul>
            </div>
            <div class="pricing-cta">
              <router-link
                to="/upgrade?from=v3_landing"
                class="button is-primary is-outlined is-fullwidth"
              >
                {{ $t('settings.change-plan') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { RouterLink } from 'vue-router';
import { ref } from 'vue';

const isProFeaturesExpanded = ref(false);
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.pricing-section {
  background: linear-gradient(135deg,
    rgba(lighten($indiefy-blue, 38%), 0.5) 0%,
    rgba(lighten($indiefy-lavender, 28%), 0.5) 100%
  );

  .body--dark & {
    background: linear-gradient(135deg,
      rgba(darken($indiefy-blue, 15%), 0.3) 0%,
      rgba(darken($indiefy-lavender, 5%), 0.3) 100%
    );
  }
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.pricing-card {
  position: relative;
  background-color: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  }

  &.is-featured {
    transform: scale(1.05);
    z-index: 2;
    border: 2px solid $indiefy-pink;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: scale(1.05) translateY(-10px);
    }

    .pricing-tier {
      color: $indiefy-pink;
    }
  }
}

.popular-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: $indiefy-pink;
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 0.3rem 1rem;
  border-bottom-left-radius: 8px;
}

.pricing-header {
  padding: 2rem 2rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .body--dark & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
}

.pricing-tier {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--color-text, #333);
}

.pricing-price {
  margin-bottom: 1rem;

  .price {
    font-family: $family-display;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-text, #333);
  }

  .billing {
    font-size: 0.9rem;
    color: var(--color-text-light, #666);
    margin-left: 0.3rem;
  }
}

.pricing-description {
  font-size: 0.95rem;
  color: var(--color-text-light, #666);
}

.pricing-features {
  padding: 1.5rem 2rem;
  flex-grow: 1;

  .feature-label {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 1rem;
    color: var(--color-text, #333);
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.5rem 0;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      color: var(--color-text, #333);

      i {
        width: 20px;
        margin-right: 10px;
        color: $indiefy-blue;
      }

      &.disabled {
        color: var(--color-text-light, #999);

        i {
          color: #ccc;

          .body--dark & {
            color: #666;
          }
        }
      }
    }
  }
}

.pricing-cta {
  padding: 1.5rem 2rem 2rem;
}

@media screen and (max-width: 768px) {
  .pricing-card {
    margin-bottom: 2rem;

    &.is-featured {
      transform: scale(1);

      &:hover {
        transform: translateY(-10px);
      }
    }
  }
}

.expand-features {
  margin-top: 1rem !important;
  justify-content: center !important;

  .expand-button {
    background: transparent;
    border: none;
    color: $indiefy-blue;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      background: rgba($indiefy-blue, 0.1);
      transform: translateY(-2px);
    }

    i {
      font-size: 0.8rem;
      transition: transform 0.3s ease;
    }
  }
}

.feature-list {
  li {
    animation: fadeIn 0.3s ease;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pricing-highlight-banner {
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 8px 32px rgba($indiefy-blue, 0.3);

  .banner-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    max-width: 600px;
    margin: 0 auto;
    text-align: center;

    .banner-icon {
      font-size: 2rem;
      color: rgba(255, 255, 255, 0.9);
    }

    .banner-text {
      h3 {
        color: white;
        margin-bottom: 0.5rem;
      }

      p {
        color: rgba(255, 255, 255, 0.9);
        margin: 0;
        font-size: 1.1rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;

    .banner-content {
      flex-direction: column;
      gap: 1rem;

      .banner-icon {
        font-size: 1.5rem;
      }

      .banner-text p {
        font-size: 1rem;
      }
    }
  }
}
</style>